import React from 'react'
import { RouteComponentProps } from 'react-router'
import { get } from 'lodash'
import { compose, graphql } from 'react-apollo'
import { Pane, Popover, majorScale, minorScale, Position } from 'evergreen-ui'
import withErrorBoundary from 'hoc/withErrorBoundary'
import NotificationCenter from '../../components/Notifications/NotificationCenter'
import NotificationsToggle from '../../components/Notifications/NotificationsToggle'
import { NotificationProps } from '../../components/Notifications/Notification'
import typewriter from 'analytics/typewriter'
import gql from 'graphql-tag'

const glqAppNotifications = gql`
  query GetAppNotificationsForWorkspace($workspaceSlug: Slug!, $after: String, $skipActionItems: Boolean!) {
    appNotifications(workspaceSlug: $workspaceSlug, after: $after) {
      counts {
        unread
      }
      nodes {
        action {
          url
        }
        category
        content
        id
        initiator
        read
        templateKey
        insertedAt
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }

    actionItemNotifications(workspaceSlug: $workspaceSlug) @skip(if: $skipActionItems) {
      actionable
      action {
        url
      }
      category
      content
      id
      initiator
      read
      templateKey
      insertedAt
    }
  }
`

interface AppNotificationsInputProps {
  workspaceSlug: string
}

interface AppNotificationsVariablesProps {
  workspaceSlug: string
  skipActionItems: boolean
}

interface AppNotificationsOutputProps {
  actionItems: NotificationProps[]
  updateItems: NotificationProps[]
  error?: unknown
  isLoading: boolean
  hasNextPage: boolean
  unreadCount: number
  onLoadMore: () => any
}

const withAppNotifications = graphql<
  AppNotificationsInputProps,
  any,
  AppNotificationsVariablesProps,
  AppNotificationsOutputProps
>(glqAppNotifications, {
  options: ({ workspaceSlug }) => ({
    variables: {
      workspaceSlug,
      skipActionItems: false
    }
  }),
  props: ({ data, ownProps }) => {
    const updateItems = get(data, 'appNotifications.nodes', [])
    const actionItems = get(data, 'actionItemNotifications', [])
    const unreadUpdateItems = get(data, 'appNotifications.counts.unread', 0)
    const unreadActionItems = actionItems.filter(n => !n.read).length
    const unreadCount = unreadActionItems + unreadUpdateItems
    const hasNextPage = get(data, 'appNotifications.pageInfo.hasNextPage', false)

    return {
      actionItems,
      error: data!.error,
      hasNextPage,
      isLoading: data!.loading,
      unreadCount,
      updateItems,
      // Naive "infinite" loading implementation
      onLoadMore() {
        return data!.fetchMore({
          variables: {
            workspaceSlug: ownProps.workspaceSlug,
            after: data!.appNotifications.pageInfo.endCursor,
            skipActionItems: true
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const oldNodes = previousResult.appNotifications.nodes
            const newNodes = fetchMoreResult.appNotifications.nodes

            if (!Array.isArray(newNodes) || !newNodes.length) {
              return previousResult
            }

            return {
              ...previousResult,
              appNotifications: {
                ...fetchMoreResult.appNotifications,
                // Append new notifications at the end of the list
                nodes: [...oldNodes, ...newNodes]
              }
            }
          }
        })
      }
    }
  }
})

const gqlMarkAllAsReadMutation = gql`
  mutation markAllAppNotificationsRead($input: AllAppNotificationsInput!) {
    markAllAppNotificationsRead(input: $input) {
      success
    }
  }
`

interface MarkAllNotificationsReadInputProps {
  workspaceSlug: string
  unreadCount: number
}

interface MarkAllNotificationsReadVariablesProps {
  input: {
    workspaceSlug: string
  }
}

interface MarkAllNotificationsReadOutputProps {
  onMarkAllAsRead: () => void
}

const withMarkAllNotificationsRead = graphql<
  MarkAllNotificationsReadInputProps,
  any,
  MarkAllNotificationsReadVariablesProps,
  MarkAllNotificationsReadOutputProps
>(gqlMarkAllAsReadMutation, {
  options: ({ workspaceSlug }) => ({
    variables: {
      input: { workspaceSlug }
    },
    refetchQueries: [
      {
        query: glqAppNotifications,
        variables: {
          workspaceSlug,
          skipActionItems: false
        }
      }
    ]
  }),
  props: ({ mutate, ownProps }) => ({
    async onMarkAllAsRead() {
      // Only run the mutation if there are unread notifications
      if (!ownProps.unreadCount) {
        return
      }

      return mutate!()
    }
  })
})

type InputProps = MarkAllNotificationsReadInputProps & AppNotificationsInputProps
type OutputProps = MarkAllNotificationsReadOutputProps & AppNotificationsOutputProps

interface NotificationCenterAppProps extends RouteComponentProps<{}, {}>, InputProps, OutputProps {
  workspaceId: string
}

class NotificationCenterApp extends React.Component<NotificationCenterAppProps> {
  componentDidUpdate(prevProps) {
    const prevHadUnreads = Boolean(prevProps.unreadCount)
    const hasUnreads = Boolean(this.props.unreadCount)

    // Track when the unreadCount would be displayed to the user
    // But to reduce noise, only do this when the count goes from 0 to 1+
    if (!prevHadUnreads && hasUnreads) {
      typewriter.unreadNotificationCountDisplayed({
        workspace_id: this.props.workspaceId,
        workspace_slug: this.props.workspaceSlug
      })
    }
  }

  onLoadMore = () => {
    this.props.onLoadMore()

    // Track when the "Load More" button is clicked
    const { workspaceId, workspaceSlug } = this.props
    typewriter.notificationCenterLoadMoreClicked({
      workspace_id: workspaceId,
      workspace_slug: workspaceSlug
    })
  }

  onNotificationClick = ({ read, templateKey }: any) => {
    // Track when the Notification Center is opened
    const { workspaceId, workspaceSlug } = this.props

    typewriter.notificationClicked({
      workspace_id: workspaceId,
      workspace_slug: workspaceSlug,
      is_unread: !read,
      template_key: templateKey
    })
  }

  onOpenNotificationCenter = () => {
    this.props.onMarkAllAsRead()

    // Track when the Notification Center is opened
    const { workspaceId, workspaceSlug, unreadCount } = this.props
    typewriter.notificationCenterOpened({
      workspace_id: workspaceId,
      workspace_slug: workspaceSlug,
      has_unread_notifications: Boolean(unreadCount)
    })
  }

  render() {
    const { actionItems, error, hasNextPage, isLoading, unreadCount, updateItems, workspaceSlug } = this.props

    return (
      <Pane display="inline-flex" alignItems="center" marginRight={minorScale(5)}>
        <Popover
          position={Position.BOTTOM_RIGHT}
          bringFocusInside={false}
          content={({ close }) => (
            <Pane width={majorScale(60)}>
              <NotificationCenter
                actionItems={actionItems}
                updateItems={updateItems}
                hasError={Boolean(error)}
                hasNextPage={hasNextPage}
                isLoading={isLoading}
                onLoadMore={this.onLoadMore}
                onNotificationClick={(item: NotificationProps) => {
                  this.onNotificationClick(item)
                  close()
                }}
                workspaceSlug={workspaceSlug}
                close={close}
              />
            </Pane>
          )}
          onOpen={this.onOpenNotificationCenter}
        >
          <NotificationsToggle unreadCount={unreadCount} />
        </Popover>
      </Pane>
    )
  }
}

export default compose(
  withErrorBoundary(),
  withAppNotifications,
  withMarkAllNotificationsRead
)(NotificationCenterApp)
